import { db } from '../../../config/firebaseConfig';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  getDoc, 
  doc,
  Timestamp,
  limit,
  orderBy,
  getCountFromServer
} from 'firebase/firestore';

// Cache for storing previously fetched data
const statsCache = {
  data: null,
  timestamp: null,
  // Cache expiration time in milliseconds (30 minutes)
  expirationTime: 30 * 60 * 1000
};

// Local storage key for backup data
const ADMIN_STATS_STORAGE_KEY = 'resumedesign_admin_stats';

// Default stats to show while loading
const defaultStats = {
  totalUsers: 0,
  newUsersWeek: 0,
  newUsersMonth: 0,
  activeSubscribers: 0,
  conversionRate: "0.00",
  totalResumes: 0,
  totalCoverLetters: 0,
  dailyNewUsers: 0,
  dailyNewSubscribers: 0,
  dailySubscriptionAmount: "0.00",
  monthlySubscriptionAmount: "0.00"
};

/**
 * Fetches admin statistics with extreme reliability
 * @param {string} userId - The ID of the user to check for admin privileges
 * @returns {Promise<Object>} - Admin statistics
 */
export const fetchAdminStats = async (userId) => {
  // First check if we have valid cached data
  const now = Date.now();
  if (statsCache.data && statsCache.timestamp && 
      now - statsCache.timestamp < statsCache.expirationTime) {
    console.log('Using cached admin stats data');
    return statsCache.data;
  }

  try {
    // Check admin access first (this is usually fast)
    const isAdmin = await checkAdminAccessWithRetry(userId);
    if (!isAdmin) {
      throw new Error('User does not have admin access');
    }
    
    // Try to load from localStorage first if no valid memory cache
    let initialStats = defaultStats;
    try {
      const storedData = localStorage.getItem(ADMIN_STATS_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        console.log('Loading initial view from localStorage');
        initialStats = parsedData.data;
      }
    } catch (e) {
      console.warn('Could not retrieve from localStorage', e);
    }
    
    // Try to fetch fresh data
    try {
      // Fetch data with separate functions for each metric to isolate failures
      const stats = await fetchIsolatedStats();
      
      // Cache the successful result
      statsCache.data = stats;
      statsCache.timestamp = Date.now();
      
      // Also save to localStorage for additional backup
      try {
        localStorage.setItem(ADMIN_STATS_STORAGE_KEY, JSON.stringify({
          data: stats,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Could not save to localStorage', e);
      }
      
      return stats;
    } catch (error) {
      console.error('Error fetching fresh stats:', error);
      
      // If we have cached data that's expired but better than nothing, use it
      if (statsCache.data) {
        console.log('Using expired memory cache data as fallback');
        return statsCache.data;
      }
      
      // Return the localStorage data we loaded earlier if available
      if (initialStats !== defaultStats) {
        return initialStats;
      }
      
      // If all else fails, return default stats
      return defaultStats;
    }
  } catch (error) {
    console.error('Error in fetchAdminStats:', error);
    throw error;
  }
};

/**
 * Fetch each stat individually to isolate failures
 */
const fetchIsolatedStats = async () => {
  // Initialize with defaults
  const stats = { ...defaultStats };
  
  // Use Promise.allSettled to get as many successful results as possible
  const results = await Promise.allSettled([
    fetchTotalUsers(),
    fetchNewUsersWeek(),
    fetchNewUsersMonth(),
    fetchActiveSubscribers(),
    fetchTotalResumes(),
    fetchTotalCoverLetters(),
    fetchDailyNewUsers(),
    fetchDailyNewSubscribers(),
    fetchFinancialData()
  ]);
  
  // Process results, keeping defaults for any that failed
  if (results[0].status === 'fulfilled') stats.totalUsers = results[0].value;
  if (results[1].status === 'fulfilled') stats.newUsersWeek = results[1].value;
  if (results[2].status === 'fulfilled') stats.newUsersMonth = results[2].value;
  if (results[3].status === 'fulfilled') stats.activeSubscribers = results[3].value;
  if (results[4].status === 'fulfilled') stats.totalResumes = results[4].value;
  if (results[5].status === 'fulfilled') stats.totalCoverLetters = results[5].value;
  if (results[6].status === 'fulfilled') stats.dailyNewUsers = results[6].value;
  if (results[7].status === 'fulfilled') stats.dailyNewSubscribers = results[7].value;
  
  // Financial data needs special handling as it has multiple values
  if (results[8].status === 'fulfilled') {
    const financialData = results[8].value;
    stats.dailySubscriptionAmount = financialData.dailyAmount;
    stats.monthlySubscriptionAmount = financialData.monthlyAmount;
  }
  
  // Calculate conversion rate after we have the required values
  stats.conversionRate = stats.totalUsers > 0 
    ? ((stats.activeSubscribers / stats.totalUsers) * 100).toFixed(2) 
    : "0.00";
  
  return stats;
};

/**
 * Individual fetch functions with retry logic
 */
const fetchWithRetry = async (fetchFn, defaultValue, retries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      console.error(`Attempt ${attempt + 1} failed:`, error);
      lastError = error;
      
      if (attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff
      }
    }
  }
  
  console.warn(`All ${retries} attempts failed, using default value`, defaultValue);
  return defaultValue;
};

// Individual fetch functions using getCountFromServer for accurate counts
const fetchTotalUsers = () => fetchWithRetry(
  async () => {
    // Use getCountFromServer for accurate counts without timing out
    const countSnapshot = await getCountFromServer(collection(db, 'users'));
    return countSnapshot.data().count;
  }, 
  0
);

const fetchNewUsersWeek = () => fetchWithRetry(
  async () => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneWeekAgoTimestamp = Timestamp.fromDate(oneWeekAgo);
    
    const q = query(
      collection(db, 'users'), 
      where('createdAt', '>=', oneWeekAgoTimestamp)
    );
    const countSnapshot = await getCountFromServer(q);
    return countSnapshot.data().count;
  }, 
  0
);

const fetchNewUsersMonth = () => fetchWithRetry(
  async () => {
    const now = new Date();
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const oneMonthAgoTimestamp = Timestamp.fromDate(oneMonthAgo);
    
    const q = query(
      collection(db, 'users'), 
      where('createdAt', '>=', oneMonthAgoTimestamp)
    );
    const countSnapshot = await getCountFromServer(q);
    return countSnapshot.data().count;
  }, 
  0
);

const fetchActiveSubscribers = () => fetchWithRetry(
  async () => {
    const q = query(
      collection(db, 'users'), 
      where('subscriptionStatus', '==', 'active')
    );
    const countSnapshot = await getCountFromServer(q);
    return countSnapshot.data().count;
  }, 
  0
);

const fetchTotalResumes = () => fetchWithRetry(
  async () => {
    const countSnapshot = await getCountFromServer(collection(db, 'resumes'));
    return countSnapshot.data().count;
  }, 
  0
);

const fetchTotalCoverLetters = () => fetchWithRetry(
  async () => {
    const countSnapshot = await getCountFromServer(collection(db, 'cover-letters'));
    return countSnapshot.data().count;
  }, 
  0
);

const fetchDailyNewUsers = () => fetchWithRetry(
  async () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayTimestamp = Timestamp.fromDate(today);
    
    const q = query(
      collection(db, 'users'), 
      where('createdAt', '>=', todayTimestamp)
    );
    const countSnapshot = await getCountFromServer(q);
    return countSnapshot.data().count;
  }, 
  0
);

const fetchDailyNewSubscribers = () => fetchWithRetry(
  async () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayTimestamp = Timestamp.fromDate(today);
    
    const q = query(
      collection(db, 'users'), 
      where('subscriptionStatus', '==', 'active'),
      where('subscriptionStartDate', '>=', todayTimestamp)
    );
    
    // This particular query might still time out, so fallback to a safer approach
    try {
      const countSnapshot = await getCountFromServer(q);
      return countSnapshot.data().count;
    } catch (e) {
      console.warn('Complex query failed, falling back to manual count');
      // Fallback to a more reliable but less efficient method
      const snapshot = await getDocs(query(
        collection(db, 'users'),
        where('subscriptionStatus', '==', 'active'),
        limit(1000)
      ));
      
      // Manually filter results
      let count = 0;
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.subscriptionStartDate && data.subscriptionStartDate >= todayTimestamp) {
          count++;
        }
      });
      
      return count;
    }
  }, 
  0
);

const fetchFinancialData = () => fetchWithRetry(
  async () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const todayTimestamp = Timestamp.fromDate(today);
    const firstDayOfMonthTimestamp = Timestamp.fromDate(firstDayOfMonth);
    
    // Split these queries for better reliability
    let dailyAmount = 0;
    let monthlyAmount = 0;
    
    // Daily amount
    try {
      const dailyQ = query(
        collection(db, 'purchases'), 
        where('createdAt', '>=', todayTimestamp)
      );
      // For small result sets, we can get all docs
      const dailySnapshot = await getDocs(dailyQ);
      
      dailySnapshot.forEach(doc => {
        const data = doc.data();
        const amount = data.amount || 0;
        const currency = data.currency || 'inr';
        
        if (currency.toLowerCase() === 'inr') {
          dailyAmount += amount / 75;
        } else {
          dailyAmount += amount;
        }
      });
    } catch (e) {
      console.error('Error fetching daily amount', e);
    }
    
    // Monthly amount - separate try/catch
    try {
      // For monthly, we might need to use paging for large datasets
      const getMonthlyPurchases = async () => {
        let lastDoc = null;
        let hasMore = true;
        let total = 0;
        
        while (hasMore) {
          let q;
          if (lastDoc) {
            q = query(
              collection(db, 'purchases'),
              where('createdAt', '>=', firstDayOfMonthTimestamp),
              orderBy('createdAt'),
              limit(500)
            );
          } else {
            q = query(
              collection(db, 'purchases'),
              where('createdAt', '>=', firstDayOfMonthTimestamp),
              orderBy('createdAt'),
              limit(500)
            );
          }
          
          const snapshot = await getDocs(q);
          
          if (snapshot.empty) {
            hasMore = false;
          } else {
            snapshot.forEach(doc => {
              const data = doc.data();
              const amount = data.amount || 0;
              const currency = data.currency || 'inr';
              
              if (currency.toLowerCase() === 'inr') {
                total += amount / 75;
              } else {
                total += amount;
              }
            });
            
            // Get the last document for pagination
            lastDoc = snapshot.docs[snapshot.docs.length - 1];
            
            // Stop if we got fewer than the limit
            if (snapshot.docs.length < 500) {
              hasMore = false;
            }
          }
        }
        
        return total;
      };
      
      monthlyAmount = await getMonthlyPurchases();
    } catch (e) {
      console.error('Error fetching monthly amount', e);
    }
    
    return {
      dailyAmount: dailyAmount.toFixed(2),
      monthlyAmount: monthlyAmount.toFixed(2)
    };
  }, 
  { dailyAmount: "0.00", monthlyAmount: "0.00" }
);

/**
 * Admin access check with retry
 */
const checkAdminAccessWithRetry = async (userId, retries = 3) => {
  return fetchWithRetry(
    () => checkAdminAccess(userId),
    false,
    retries
  );
};

/**
 * Checks if a user has admin access
 * @param {string} userId - The ID of the user to check
 * @returns {Promise<boolean>} - Whether the user has admin access
 */
const checkAdminAccess = async (userId) => {
  try {
    // Fetch the admin configuration document
    const adminDocRef = doc(db, 'admin', process.env.REACT_APP_ADMIN_DOC_ID || 'IStA6fJzjiHovjcz6B0V');
    const adminDoc = await getDoc(adminDocRef);
    
    if (!adminDoc.exists()) {
      console.error('Admin document does not exist');
      return false;
    }
    
    const adminData = adminDoc.data();
    
    // Check if the user's email is in the admins array
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.error('User document does not exist');
      return false;
    }
    
    const userData = userDoc.data();
    const userEmail = userData.email;
    
    // Check if userEmail is in the adminUser array
    return adminData.adminUser && adminData.adminUser.includes(userEmail);
  } catch (error) {
    console.error('Error checking admin access:', error);
    return false;
  }
};